import PropTypes from 'prop-types';
import React from 'react';

import { colorList, colorSchemaList, fontSizeList, fontWeightList } from '../../../typings/design-tokens';
import { MatchStatusList, MatchStatusType } from '../../../typings/match';
import { CtaTarget, CtaTargets } from '../../../typings/match-row';
import { isPropTypeNullable } from '../../../utils/prop-types';
import { getMappedColor, IClassNames, makeClassNames } from '../../../utils/styles';
import Text, { IText } from '../../atoms/text/text';
import { IMatchRow } from '../match-row/match-row';
import { ITeam } from '../team/team';

import {
  colorSchemaMapping,
  DEFAULT_COLOR,
  DEFAULT_FONT_SIZE,
  DEFAULT_FONT_WEIGHT,
  MatchChangeVariant,
} from './match-change';
import { getGoalByStatus } from './match-change-helpers';
import { MATCH_CHANGE_TYPE_HP } from './match-change-hp';

export const MATCH_CHANGE_TYPE_LMP = 'lmp';
interface IMatchChangeTeam extends Pick<ITeam, 'id' | 'goal'> {
  shortName: string;
}
interface IMatchChangeRow
  extends Pick<
    IMatchRow,
    'id' | 'link' | 'status' | 'color' | 'colorSchema' | 'fontSize' | 'fontWeight' | 'target' | 'trackingData'
  > {
  away: IMatchChangeTeam;
  home: IMatchChangeTeam;
}
export interface IMatchChangeLmp extends IMatchChangeRow {
  isSelected?: boolean;
  variant?: MatchChangeVariant;
}

export const classNames = (isSelected: boolean): IClassNames =>
  makeClassNames({
    wrapper: [
      'match-change--wrapper',
      `match-change--wrapper-${MATCH_CHANGE_TYPE_LMP}`,
      isSelected && 'match-change--wrapper-selected',
    ],
    table: ['match-change--table', isSelected && 'match-change--table-selected'], // for SELECTION BORDER padding
    row: 'match-change--row',
    team: 'match-change--team',
    goal: 'match-change--goal',
  });

export const MatchChangeLmp: React.FC<IMatchChangeLmp> = ({
  away,
  /* istanbul ignore next: default value */ color = DEFAULT_COLOR,
  /* istanbul ignore next: default value */ fontSize = DEFAULT_FONT_SIZE,
  /* istanbul ignore next: default value */ fontWeight = DEFAULT_FONT_WEIGHT,
  /* istanbul ignore next: default value */ isSelected = false,
  /* istanbul ignore next: default value */ trackingData = {},
  colorSchema,
  home,
  link,
  status,
  target,
}) => {
  const { /* istanbul ignore next: default value */ goal: homeGoal = '-', shortName: homeName } = home;
  const { /* istanbul ignore next: default value */ goal: awayGoal = '-', shortName: awayName } = away;

  const cn = classNames(isSelected);
  const colorToUse = getMappedColor({ colorSchema, colorSchemaMapping, defaultColor: color });
  const attrsText: IText = isSelected
    ? { tag: 'span' }
    : {
        tag: 'a', // TAG should be a CLICKABLE ANCHOR only if MATCH is NOT selected
        href: link ?? undefined,
        target,
        dataAttributes: trackingData,
      };

  return (
    <Text {...attrsText} className={cn.wrapper} fontWeight={fontWeight} fontSize={fontSize} color={colorToUse}>
      <div className={cn.table}>
        <div className={cn.row}>
          <span className={cn.team}>{homeName.toUpperCase()}</span>
          <span className={cn.goal}>{getGoalByStatus(homeGoal, status)}</span>
        </div>
        <div className={cn.row}>
          <span className={cn.team}>{awayName.toUpperCase()}</span>
          <span className={cn.goal}>{getGoalByStatus(awayGoal, status)}</span>
        </div>
      </div>
    </Text>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  MatchChangeLmp.propTypes = {
    away: PropTypes.exact({
      goal: isPropTypeNullable(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
      id: isPropTypeNullable(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
      shortName: isPropTypeNullable(PropTypes.string),
    }).isRequired,
    color: PropTypes.oneOf(colorList),
    colorSchema: PropTypes.oneOf(colorSchemaList),
    fontSize: PropTypes.oneOf(fontSizeList),
    fontWeight: PropTypes.oneOf(fontWeightList),
    home: PropTypes.exact({
      goal: isPropTypeNullable(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
      id: isPropTypeNullable(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
      shortName: isPropTypeNullable(PropTypes.string),
    }).isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isSelected: PropTypes.bool,
    link: PropTypes.string,
    status: PropTypes.oneOf<MatchStatusType>(MatchStatusList).isRequired,
    target: isPropTypeNullable(PropTypes.oneOf<CtaTarget>(CtaTargets)),
    trackingData: PropTypes.object,
    variant: PropTypes.oneOf<MatchChangeVariant>([MATCH_CHANGE_TYPE_LMP, MATCH_CHANGE_TYPE_HP]), // FIXME this should not be necessary here?
  };
}
